import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash, Warning } from "@phosphor-icons/react";

const ENTITY_LABEL = { product: "product", series: "series", manufacturer: "manufacturer" };

// Fetch active stock for an entity. On failure returns error:true (callers must fail closed).
export async function fetchStockSummary(entityType, id) {
  try {
    const { data } = await api.get(`/stock-summary/${entityType}/${id}`);
    return { ...data, error: false };
  } catch {
    return { has_stock: false, error: true, summary: "" };
  }
}

// Delete button that first checks for attached active stock. If stock exists it BLOCKS
// the delete and explains why; otherwise it shows the normal delete confirmation.
export function DeleteStockGuard({ entityType, id, name, isStaff, onConfirm, testId }) {
  const [open, setOpen] = useState(false);
  const [stock, setStock] = useState(null); // null = loading

  const openGuard = async () => {
    setStock(null);
    setOpen(true);
    setStock(await fetchStockSummary(entityType, id));
  };
  const label = ENTITY_LABEL[entityType] || "item";
  const blocked = stock?.has_stock;
  const errored = stock?.error;

  return (
    <>
      <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={openGuard} data-testid={testId}>
        <Trash size={14} />
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="delete-guard-dialog">
          {stock === null ? (
            <AlertDialogHeader>
              <AlertDialogTitle>Checking stock…</AlertDialogTitle>
              <AlertDialogDescription>Making sure it’s safe to delete {name}.</AlertDialogDescription>
            </AlertDialogHeader>
          ) : errored ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <Warning size={20} weight="fill" className="text-amber-600" /> Couldn’t check stock
                </AlertDialogTitle>
                <AlertDialogDescription>
                  We couldn’t verify the stock attached to {name}, so deletion is blocked for safety. Please try again.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-md" data-testid="delete-guard-close">Close</AlertDialogCancel>
                <AlertDialogAction onClick={(e) => { e.preventDefault(); openGuard(); }} className="rounded-md" data-testid="delete-guard-retry">Retry</AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : blocked ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <Warning size={20} weight="fill" className="text-amber-600" /> Can’t delete {name}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This {label} still has active stock attached: <strong>{stock.summary}</strong>.
                  Use up, archive or move that stock first, then delete.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-md" data-testid="delete-guard-close">Close</AlertDialogCancel>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  {isStaff
                    ? "This will be sent to an admin for approval."
                    : `This permanently removes the ${label} and everything under it. This cannot be undone.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-md">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => { setOpen(false); onConfirm(); }}
                  className="bg-red-600 hover:bg-red-700 rounded-md"
                  data-testid="delete-guard-confirm"
                >
                  {isStaff ? "Request delete" : "Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// Confirmation shown before saving an EDIT to an entity that has active stock attached.
export function EditStockConfirm({ open, onOpenChange, entityType, name, summary, onConfirm }) {
  const label = ENTITY_LABEL[entityType] || "item";
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="edit-guard-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Warning size={20} weight="fill" className="text-amber-600" /> {name} has active stock
          </AlertDialogTitle>
          <AlertDialogDescription>
            This {label} currently has active stock attached: <strong>{summary}</strong>.
            Existing rolls won’t change, but please double-check this edit is intended. Save changes anyway?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-md">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-blue-600 hover:bg-blue-700 rounded-md" data-testid="edit-guard-confirm">
            Save anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
